import BackButton from '@/components/BackButton';
import CtaButton, { SecondaryCtaButton } from '@/components/CtaButton';
import CtaText from '@/components/CtaText';
import ErrorMessage from '@/components/ErrorMessage';
import TextInput from '@/components/Forms/TextInput';
import LoadingSpinner from '@/components/Icons/LoadingSpinner';
import RightCheveron from '@/components/Icons/RightCheveron';
import PageLayout from '@/components/PageLayout';
import { NavBarLightTheme } from '@/components/PageLayout/PageNavBar';
import TwelveColGrid from '@/components/TwelveColGrid';
import useHandleErrorLogic from '@/hooks/useHandleErrorLogic';
import { resetPasswordRequest } from '@/utils/http/userAuthRequests';
import { CLIENT_LOGIN_URL } from '@/utils/constants';
import { Field, Form, Formik } from 'formik';
import router from 'next/router';
import { useState } from 'react';
import * as yup from 'yup';
import axios from 'axios';

const PasswordForgotSchema = yup.object().shape({
  email: yup.string().email().required('Please provide a valid email'),
});

const steps = {
  email: 'email',
  confirmation: 'confirmation',
};

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(steps.email);
  const [err, setErr] = useHandleErrorLogic();

  return (
    <PageLayout>
      <NavBarLightTheme sticky={false} />
      <ErrorMessage error={err} />
      {step === steps.email && (
        <TwelveColGrid my="mt-3.2 lgt:mt-6.4 mb-20 lgt:mb-32">
          <div className="flex justify-center col-span-12">
            <BackButton href={CLIENT_LOGIN_URL} />
          </div>

          <h3 className="col-span-12 mt-1.6  text-center text-black-100 copy-l lgt:col-span-6 lgt:col-start-4">
            FORGOT YOUR PASSWORD?
          </h3>

          <h3 className="col-span-12 text-center text-body text-black-300 mt-1.6">
            We&apos;ll email you instructions on how to reset your password
          </h3>
          <div className="col-span-12 lgt:col-start-5 lgt:col-span-4 mt-4.8">
            <Formik
              validationSchema={PasswordForgotSchema}
              initialValues={{
                email: '',
              }}
              onSubmit={async (values, actions) => {
                try {
                  const res = await resetPasswordRequest({ ...values });
                  actions.setSubmitting(false);
                  if (res.status === 200) {
                    setStep('confirmation');
                  }
                } catch (error) {
                  if (axios.isAxiosError(error)) {
                    const errorMessage = error.response?.data.error.message;
                    setErr(errorMessage);
                  } else {
                    setErr(false);
                  }
                  actions.setSubmitting(false);
                }
              }}
            >
              {({ isValid, isSubmitting }) => (
                <Form className="flex flex-col justify-between">
                  <div>
                    <Field
                      type="email"
                      name="email"
                      labelCopy="Email"
                      placeholder=" "
                      component={TextInput}
                    />
                  </div>
                  <div className="flex flex-col justify-center w-full max-w-34.4 mx-auto mt-3.2 absolute bottom-40 left-0 right-0">
                    <CtaButton
                      height="h-4.8"
                      width="w-34.4 lgt:w-36.0"
                      type="submit"
                      disabled={isSubmitting || !isValid}
                    >
                      {isSubmitting ? (
                        <>
                          <CtaText>PROCESSING</CtaText>
                          <LoadingSpinner />
                        </>
                      ) : (
                        <>
                          <CtaText>RESET PASSWORD</CtaText>
                          <RightCheveron />
                        </>
                      )}
                    </CtaButton>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </TwelveColGrid>
      )}
      {step === steps.confirmation && (
        <TwelveColGrid my="mt-3.2 lgt:mt-6.4 mb-20 lgt:mb-32">
          <div className="flex justify-center col-span-12">
            <BackButton href={CLIENT_LOGIN_URL} />
          </div>
          <h3 className="col-span-12 mt-1.6 font-medium text-center copy-l">
            NO WORRIES
          </h3>

          <h3 className="col-span-12 text-center text-body text-black-200">
            Email with the new password link is coming your way
          </h3>

          <div className="col-span-12 flex justify-center mt-3.2">
            <SecondaryCtaButton
              height="h-4.8"
              width="w-34.4 lgt:w-36.0"
              onClick={() => router.reload()}
            >
              <CtaText>TRY AGAIN</CtaText>
              <RightCheveron />
            </SecondaryCtaButton>
          </div>
          <h3 className="col-span-12 lgt:col-start-5 lgt:col-span-4 mt-1.6 text-center text-body text-black-300">
            When you get it, just click through to confirm your email address
            and set up a new password.
          </h3>
        </TwelveColGrid>
      )}
    </PageLayout>
  );
}
